/**
 * Capability Detail Page
 *
 * IMPORTANT: This is a control plane UI, not an AI execution app.
 * - Capability = metadata, not logic
 * - Admin manages configuration state only
 * - Execution logic lives in backend execution engine
 * - UI never executes AI
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  IconButton,
  Chip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ArrowLeft, Save, Plus, Pencil, X } from 'lucide-react';
import { useCapability, useUpdateCapability } from '../../services/capability.service';
import {
  useCapabilityModels,
  useToggleCapabilityModelStatus,
} from '../../services/capability-model.service';
import { MODEL_COST_TIER_LABELS, type ModelCostTier } from '../../common/model-tiers';
import { Capability, CapabilityModel } from '../../types/admin';
import { CapabilityModelFormDialog } from '../../components/admin/CapabilityModelFormDialog';
import { VideoServiceSettingsSection } from '../../features/admin/generate-video/components/VideoServiceSettingsSection';
import { isVideoServiceConfig } from '../../features/admin/generate-video/utils';

// ============================================
// Capability Metadata Section Component
// ============================================

interface CapabilityMetadataSectionProps {
  capability: Capability;
  isEditing: boolean;
  onEditToggle: () => void;
}

const CapabilityMetadataSection: React.FC<CapabilityMetadataSectionProps> = ({
  capability,
  isEditing,
  onEditToggle,
}) => {
  const updateMutation = useUpdateCapability();
  const [formValues, setFormValues] = useState({
    name: capability.name,
    description: capability.description || '',
    configSchema: capability.configSchema || '',
    isActive: capability.isActive,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when capability changes
  React.useEffect(() => {
    setFormValues({
      name: capability.name,
      description: capability.description || '',
      configSchema: capability.configSchema || '',
      isActive: capability.isActive,
    });
    setErrors({});
  }, [capability]);

  const handleChange = (field: string, value: string | boolean) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formValues.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formValues.configSchema && formValues.configSchema.trim()) {
      try {
        JSON.parse(formValues.configSchema);
      } catch {
        newErrors.configSchema = 'Config schema must be valid JSON';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      await updateMutation.mutateAsync({
        publicId: capability.publicId,
        name: formValues.name.trim(),
        description: formValues.description?.trim() || undefined,
        configSchema: formValues.configSchema?.trim() || undefined,
        isActive: formValues.isActive,
      });
      onEditToggle();
    } catch (error) {
      console.error('Error updating capability:', error);
    }
  };

  const handleCancel = () => {
    setFormValues({
      name: capability.name,
      description: capability.description || '',
      configSchema: capability.configSchema || '',
      isActive: capability.isActive,
    });
    setErrors({});
    onEditToggle();
  };

  return (
    <Paper className="rounded-xl p-6 shadow-sm">
      <Box className="mb-4 flex items-center justify-between">
        <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
          Capability Metadata
        </Typography>
        {!isEditing && (
          <Button
            variant="outlined"
            size="small"
            onClick={onEditToggle}
            className="border-blue-600 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400"
          >
            Edit
          </Button>
        )}
      </Box>

      <Box className="space-y-4">
        {/* Name Field */}
        <TextField
          fullWidth
          label="Name"
          value={formValues.name}
          onChange={(e) => handleChange('name', e.target.value)}
          error={!!errors.name}
          helperText={errors.name || 'Display name for the capability'}
          disabled={!isEditing || updateMutation.isPending}
          required
        />

        {/* Description Field */}
        <TextField
          fullWidth
          label="Description"
          value={formValues.description}
          onChange={(e) => handleChange('description', e.target.value)}
          helperText="Brief description of what this capability does"
          disabled={!isEditing || updateMutation.isPending}
          multiline
          rows={2}
        />

        {/* Code Field - Read-only after creation */}
        <TextField
          fullWidth
          label="Key/Code"
          value={capability.code}
          helperText="Unique identifier (read-only after creation)"
          disabled
          InputProps={{
            style: { fontFamily: 'monospace' },
          }}
        />

        {/* Config Schema Field */}
        <TextField
          fullWidth
          label="Config Schema (JSON)"
          value={formValues.configSchema}
          onChange={(e) => handleChange('configSchema', e.target.value)}
          error={!!errors.configSchema}
          helperText={errors.configSchema || 'Optional JSON configuration schema'}
          disabled={!isEditing || updateMutation.isPending}
          multiline
          rows={4}
          InputProps={{
            style: { fontFamily: 'monospace', fontSize: '0.875rem' },
          }}
        />

        {/* Status Field */}
        <FormControlLabel
          control={
            <Switch
              checked={formValues.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              disabled={!isEditing || updateMutation.isPending}
            />
          }
          label="Active"
        />

        {/* Action Buttons */}
        {isEditing && (
          <Box className="flex gap-2 pt-2">
            <Button
              variant="contained"
              startIcon={
                updateMutation.isPending ? <CircularProgress size={16} /> : <Save size={16} />
              }
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<X size={16} />}
              onClick={handleCancel}
              disabled={updateMutation.isPending}
              className="border-gray-300 text-gray-600 dark:border-slate-600 dark:text-slate-400"
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// ============================================
// Capability Detail Page Component
// ============================================

export const CapabilityDetail: React.FC = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<CapabilityModel | null>(null);

  // Fetch capability and its models
  const {
    data: capability,
    isLoading: isLoadingCapability,
    error: capabilityError,
  } = useCapability(publicId || '');

  const { data: models, isLoading: isLoadingModels } = useCapabilityModels(
    capability?.publicId || ''
  );
  const isVideoService = isVideoServiceConfig(
    capability?.effectiveConfigSchema ?? capability?.configSchema
  );

  const sortedModels = React.useMemo(() => {
    if (!models) return [];
    const costTierOrder: Record<string, number> = {
      cheap: 0,
      normal: 1,
      expensive: 2,
    };
    return [...models].sort((a, b) => {
      // 1. Active mappings before inactive mappings
      if (a.isActive !== b.isActive) {
        return a.isActive ? -1 : 1;
      }
      // 2. Group by cost tier
      const tierA = costTierOrder[a.costTier] ?? 99;
      const tierB = costTierOrder[b.costTier] ?? 99;
      if (tierA !== tierB) {
        return tierA - tierB;
      }
      // 3. Priority descending (higher number runs first)
      if (b.priority !== a.priority) {
        return b.priority - a.priority;
      }
      // 4. Stable tie-breaker id asc
      return a.id - b.id;
    });
  }, [models]);

  const toggleModelStatusMutation = useToggleCapabilityModelStatus();

  // Column definitions for CapabilityModel table
  // Note: UI reflects capabilityModel schema from backend
  const modelColumns: GridColDef[] = [
    {
      field: 'providerCode',
      headerName: 'Provider',
      width: 130,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" className="text-gray-800 dark:text-slate-200">
            {params.row.providerName || params.value}
          </Typography>
          {params.row.providerName && (
            <Typography variant="caption" className="font-mono text-gray-500">
              {params.value}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'modelCode',
      headerName: 'Model',
      flex: 1.2,
      minWidth: 260,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" className="text-gray-800 dark:text-slate-200">
            {params.row.modelName || params.value}
          </Typography>
          <Typography variant="caption" className="font-mono text-gray-500">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'costTier',
      headerName: 'Cost Tier',
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={MODEL_COST_TIER_LABELS[params.value as ModelCostTier] ?? params.value}
        />
      ),
    },
    {
      field: 'priority',
      headerName: 'Priority (High \u2192 Low)',
      width: 150,
      renderCell: (params) => (
        <Box className="flex items-center gap-1.5">
          <Typography variant="body2" className="font-semibold text-gray-900 dark:text-slate-100">
            {params.value}
          </Typography>
          <Typography variant="caption" className="text-[11px] text-gray-500 dark:text-slate-400">
            (priority #{params.value})
          </Typography>
        </Box>
      ),
    },
    {
      field: 'isDefault',
      headerName: 'Default',
      width: 100,
      renderCell: (params) =>
        params.value ? <Chip size="small" color="primary" label="Default" /> : null,
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Switch
          checked={params.value}
          onChange={(e) => {
            toggleModelStatusMutation.mutate({
              id: params.row.id,
              isActive: e.target.checked,
            });
          }}
          disabled={toggleModelStatusMutation.isPending}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => {
            setSelectedModel(params.row as CapabilityModel);
            setModelDialogOpen(true);
          }}
          className="text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
          title="Edit"
        >
          <Pencil size={16} />
        </IconButton>
      ),
    },
  ];

  // Loading state
  if (isLoadingCapability) {
    return (
      <Box className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-900">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (capabilityError || !capability) {
    return (
      <Box className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
        <Alert severity="error">
          Failed to load capability details. Please try again or go back to the list.
        </Alert>
        <Button
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate('/capabilities')}
          className="mt-4 text-gray-600 dark:text-slate-400"
        >
          Back to Capabilities
        </Button>
      </Box>
    );
  }

  return (
    <Box className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
      {/* Header */}
      <Box className="mb-6">
        <Button
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate('/capabilities')}
          className="mb-4 text-gray-600 dark:text-slate-400"
        >
          Back to Capabilities
        </Button>
        <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
          {capability.name}
        </Typography>
        <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
          Manage capability configuration and model mappings
        </Typography>
      </Box>

      {/* Capability Metadata Section */}
      <Box className="mb-6">
        <CapabilityMetadataSection
          capability={capability}
          isEditing={isEditingMetadata}
          onEditToggle={() => setIsEditingMetadata(!isEditingMetadata)}
        />
      </Box>

      {isVideoService && (
        <Box className="mb-6">
          <VideoServiceSettingsSection capability={capability} />
        </Box>
      )}

      {/* CapabilityModel Management Section */}
      <Paper className="rounded-xl p-6 shadow-sm">
        <Box className="mb-4 flex items-center justify-between">
          <Box>
            <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
              Model Mappings
            </Typography>
            <Typography variant="body2" className="mt-1 text-gray-600 dark:text-slate-400">
              Models bound to this capability. Higher priority is selected first.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => {
              setSelectedModel(null);
              setModelDialogOpen(true);
            }}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            Add Model
          </Button>
        </Box>

        <Divider className="my-4" />

        {/* Models Table */}
        {isLoadingModels ? (
          <Box className="flex items-center justify-center p-12">
            <CircularProgress />
          </Box>
        ) : sortedModels.length > 0 ? (
          <DataGrid
            rows={sortedModels}
            columns={modelColumns}
            getRowId={(row) => row.id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            disableRowSelectionOnClick
            autoHeight
            className="border-0"
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'background.default',
                borderBottom: '2px solid',
                borderColor: 'divider',
              },
            }}
          />
        ) : (
          <Box className="p-12 text-center">
            <Typography className="mb-4 text-gray-600 dark:text-slate-400">
              No models bound to this capability yet
            </Typography>
            <Typography variant="body2" className="text-gray-500 dark:text-slate-500">
              Add model mappings to define which models this capability can use
            </Typography>
          </Box>
        )}
      </Paper>

      {/* CapabilityModel Form Dialog */}
      {capability && (
        <CapabilityModelFormDialog
          open={modelDialogOpen}
          onClose={() => {
            setModelDialogOpen(false);
            setSelectedModel(null);
          }}
          capabilityPublicId={capability.publicId}
          model={selectedModel}
          compatibleTaskTypeCodes={capability.compatibleTaskTypeCodes}
          existingModels={models}
          showVideoSettings={isVideoService}
        />
      )}
    </Box>
  );
};
